import React, { useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { MailIcon, MapPinIcon, BookIcon, HeadphonesIcon, ShoppingBagIcon, MusicIcon, CalendarIcon, UsersIcon, UserIcon, ImageIcon, StarIcon, ChevronRightIcon, GiftIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { useAfterTideFilter } from '../../hooks/useAfterTideFilter';
import { samplePromotionalGraphics } from '../../data/samplePromotionalGraphics';
import BackButton from '../../components/UI/BackButton';
import { AboutMeTab } from './AboutMeTab';
import { BooksTab } from './BooksTab';
import { GraphicsTab } from './GraphicsTab';
import { CollaborationsTab, AudiobooksTab, MerchandiseTab, PlaylistsTab, EventsTab } from './ContentTabs';
import { authorData, authorBooks, audiobooks, merchandise, playlists, events, similarAuthors } from './data';
export default function AuthorProfile() {
  const {
    slug
  } = useParams();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const {
    platformData
  } = useAfterTideFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ['about', 'books', 'collaborations', 'audiobooks', 'merchandise', 'playlists', 'events', 'graphics'];
  const [activeTab, setActiveTabState] = useState(tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'about');
  const setActiveTab = (tab: string) => {
    setActiveTabState(tab);
    setSearchParams(tab === 'about' ? {} : {
      tab
    }, {
      replace: true
    });
  };
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const tabs = [{
    id: 'about',
    label: 'About',
    icon: UserIcon
  }, {
    id: 'books',
    label: 'Books',
    icon: BookIcon,
    count: authorData.booksPublished
  }, {
    id: 'collaborations',
    label: 'Co-Writes',
    icon: UsersIcon,
    count: 1
  }, {
    id: 'audiobooks',
    label: 'Audiobooks',
    icon: HeadphonesIcon,
    count: 2
  }, {
    id: 'merchandise',
    label: 'Merch',
    icon: ShoppingBagIcon,
    count: 2
  }, {
    id: 'playlists',
    label: 'Playlists',
    icon: MusicIcon,
    count: 2
  }, {
    id: 'events',
    label: 'Events',
    icon: CalendarIcon,
    count: 3
  }, {
    id: 'graphics',
    label: 'Graphics',
    icon: ImageIcon
  }];
  const authorGraphics = samplePromotionalGraphics.filter(graphic => graphic.authorId === 'elena-richards');
  const featuredBooks = [...authorBooks.standalone.slice(0, 2).map(book => ({
    ...book,
    seriesName: '',
    seriesNumber: 0,
    spiceLevel: book.heatLevel || 0,
    series: undefined
  })), ...(authorBooks.series[0]?.books || []).slice(0, 2).map(book => ({
    ...book,
    seriesName: 'Echo Series',
    seriesNumber: (book as any).seriesPosition || 0,
    spiceLevel: book.heatLevel || 0,
    series: undefined
  }))];
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`} data-id="element-7140" data-mp="meqhu5o6kajn">
      {/* Immersive Hero Section */}
      <div className="relative overflow-hidden" data-id="element-7141" data-mp="clsyjxbd6hjz">
        {/* Blurred Background */}
        <div className="absolute inset-0 z-0" data-id="element-7142" data-mp="10c5dvt8xvgkl">
          <div className={`absolute inset-0 ${darkMode ? 'bg-gray-900/90' : 'bg-white/90'} z-10`} data-id="element-7143" data-mp="10b3yjckw47x2" />
          <div className={`absolute inset-0 bg-gradient-to-b ${darkMode ? 'from-indigo-900/40 to-gray-900' : 'from-indigo-100/50 to-gray-50'} z-10`} data-id="element-7144" data-mp="3cmqdkdaavh97" />
          <img src={authorData.aestheticImages[0]} alt="" className="w-full h-full object-cover filter blur-3xl opacity-50 scale-110" data-id="element-7145" data-mp="38jtr2bhn30zz" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-7146" data-mp="2yos1xfnd5yxe">
          <div className="mb-6" data-id="element-7147" data-mp="4s2mxhp554g">
            <BackButton className={darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-indigo-600'} data-id="element-7148" data-mp="3rbmi40d5ec52" />
          </div>

          <div className="flex flex-col md:flex-row gap-8 items-start relative z-10" data-id="element-7149" data-mp="3hml42fq5y4tq">
            {/* Avatar */}
            <div className="flex-shrink-0 relative" data-id="element-7150" data-mp="iwl0b57pneii">
              <div className="w-32 h-32 md:w-48 md:h-48 rounded-full border-4 border-white dark:border-gray-900 overflow-hidden shrink-0 shadow-xl bg-white" data-id="element-7151" data-mp="e52zch5otdxj">
                <img src={authorData.avatar} alt={authorData.name} className={`w-full h-full rounded-full object-cover border-4 ${darkMode ? 'border-gray-800' : 'border-white'}`} data-id="element-7152" data-mp="lhsfpih95thb" />
              </div>
              <div className={`absolute -bottom-2 -right-2 px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1 ${darkMode ? 'bg-green-900/80 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-7153" data-mp="10drt98n4sltu">
                <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-7154" data-mp="19c3ib68cnbk6" />
                Open for Events
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0" data-id="element-7155" data-mp="1j2ofcp8dk80z">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-7156" data-mp="1j5u7yeg7ps1g">
                <div data-id="element-7157" data-mp="1o19jgzqqzbup">
                  <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-id="element-7158" data-mp="1s463muvxrg0r">
                    {authorData.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm mb-4" data-id="element-7159" data-mp="3p5k0w5g4v1ak">
                    <div className="flex items-center gap-1.5" data-id="element-7160" data-mp="3k7ignh6z0vxi">
                      <MapPinIcon className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7161" data-mp="1srgacq5sf3s" />
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-600'} data-id="element-7162" data-mp="3t5m62ol2ucul">
                        {authorData.location}
                      </span>
                    </div>
                    {authorData.genres.map((genre, idx) => <span key={idx} className={`px-3 py-1 rounded-full text-sm font-medium ${dk.cardSubtle} ${rt.textPrimary}`} data-id="element-7163" data-mp="kqat7rfqmhvh" data-mpi={idx}>
                        {genre}
                      </span>)}
                  </div>
                </div>

                <div className="flex gap-3" data-id="element-7164" data-mp="28cwzf9vc4nrg">
                  <button className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm flex items-center gap-2" data-id="element-7165" data-mp="28dztfqnyf6bl">
                    <MailIcon className="w-4 h-4" data-id="element-7166" data-mp="1ynwzno90doc7" />
                    Contact
                  </button>
                  <button className={`px-5 py-2.5 rounded-lg text-sm font-medium border transition-colors flex items-center gap-2 ${darkMode ? 'border-gray-600 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-7167" data-mp="23leygltban6h">
                    Follow
                  </button>
                </div>
              </div>

              {/* Bio with Truncation */}
              <div className="mb-6 max-w-3xl" data-id="element-7168" data-mp="1kmkkvg5l3srw">
                <p className={`leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-600'} ${!isBioExpanded ? 'line-clamp-2' : ''}`} data-id="element-7169" data-mp="1kpgi129lydfa">
                  {authorData.bio}
                </p>
                <button onClick={() => setIsBioExpanded(!isBioExpanded)} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-1 inline-flex items-center" data-id="element-7170" data-mp="rg57slix1ya2">
                  {isBioExpanded ? 'Read Less' : 'Read More'}
                  <ChevronRightIcon className={`w-3 h-3 ml-0.5 transition-transform ${isBioExpanded ? 'rotate-90' : ''}`} data-id="element-7171" data-mp="dp03v78imjtx" />
                </button>
              </div>

              {/* Stats Row */}
              <div className={`flex flex-wrap gap-6 sm:gap-10 pt-6 border-t ${darkMode ? 'border-gray-700/50' : 'border-gray-200'}`} data-id="element-7172" data-mp="3pspcgq1oj58">
                <div className="flex flex-col" data-id="element-7173" data-mp="3qvjrp4uu288b">
                  <div className="flex items-center gap-1 mb-1" data-id="element-7174" data-mp="3qwv0jj34y66a">
                    <span className="text-2xl font-bold" data-id="element-7175" data-mp="1t6a9y1owctdd">
                      {authorData.rating}
                    </span>
                    <div className="flex text-amber-400" data-id="element-7176" data-mp="1p5hz540dmgez">
                      <StarIcon className="w-5 h-5 fill-current" data-id="element-7177" data-mp="3721tienc5xnd" />
                    </div>
                  </div>
                  <span className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7178" data-mp="3c03dd1sl60zw">
                    Rating
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-7179" data-mp="3ksf0adohr6b4">
                  <span className="text-2xl font-bold mb-1" data-id="element-7180" data-mp="2o3h4qt8x34cw">
                    {authorData.booksPublished}
                  </span>
                  <span className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7181" data-mp="2t2kdano3vspm">
                    Books
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-7182" data-mp="cdevle7z7aj2">
                  <span className="text-2xl font-bold mb-1" data-id="element-7183" data-mp="cclmi6ljo8xr">
                    {authorData.seriesCount}
                  </span>
                  <span className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7184" data-mp="heknj9ttatrj">
                    Series
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-7185" data-mp="1a21igbke4f7p">
                  <span className="text-2xl font-bold mb-1" data-id="element-7186" data-mp="1a4e717knlntq">
                    {authorData.followers}
                  </span>
                  <span className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7187" data-mp="hdvwxmliim6t">
                    Followers
                  </span>
                </div>

                <div className="flex flex-col" data-id="element-7188" data-mp="ra8ooexhypyx">
                  <div className="flex items-center gap-1 mb-1" data-id="element-7189" data-mp="vd5cql5t3ea9">
                    <span className="text-2xl font-bold text-rose-500" data-id="element-7190" data-mp="vbap2z35h82h">
                      $1.2k
                    </span>
                  </div>
                  <span className={`text-xs uppercase tracking-wider font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7191" data-mp="26rsnh4qb5krr">
                    Donated
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className={`sticky top-0 z-30 border-b backdrop-blur-sm ${darkMode ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-gray-200'}`} data-id="element-7192" data-mp="3ig6lnludk4fm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7193" data-mp="38l6d4vo8cmca">
          <div className="flex overflow-x-auto scrollbar-hide" data-id="element-7194" data-mp="33la4uk8i28qx">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative flex items-center gap-2 py-4 px-6 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? 'text-indigo-600' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-7195" data-mp="2ul3w1bnqxwy3">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : ''}`} data-id="element-7196" data-mp="1go29pw7iewb4" />
                  {tab.label}
                  {tab.count !== undefined && <span className={`ml-1.5 text-xs py-0.5 px-2 rounded-full ${isActive ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-7197" data-mp="17vqvd8po6zbi">
                      {tab.count}
                    </span>}
                  {isActive && <motion.div layoutId="activeAuthorTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" data-id="element-7198" data-mp="1qm8c0ex8a4gi" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-id="element-7199" data-mp="7mr7h5bmdwpt">
        <AnimatePresence mode="wait" data-id="element-7200" data-mp="2ozcu3jlbwhx">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-7201" data-mp="2nxurf45q9qp">
            {activeTab === 'about' && <AboutMeTab author={authorData} data-id="element-7202" data-mp="3g5glx8hstwvb" />}

            {activeTab === 'books' && <BooksTab books={authorBooks} data-id="element-7203" data-mp="3tw256yb608tq" />}

            {activeTab === 'collaborations' && <CollaborationsTab collaborations={authorBooks.collaborations} data-id="element-7204" data-mp="3p3920evch8jr" />}

            {activeTab === 'audiobooks' && <AudiobooksTab audiobooks={audiobooks} authorName={authorData.name} data-id="element-7205" data-mp="31cmsej5ozg7n" />}

            {activeTab === 'merchandise' && <MerchandiseTab merchandise={merchandise} data-id="element-7206" data-mp="3a46ivdinilda" />}

            {activeTab === 'playlists' && <PlaylistsTab playlists={playlists} data-id="element-7207" data-mp="3ez3o09ct8akw" />}

            {activeTab === 'events' && <EventsTab events={events} data-id="element-7208" data-mp="3jyoporjd6x1b" />}

            {activeTab === 'graphics' && <GraphicsTab authorId="elena-richards" authorName={authorData.name} graphics={authorGraphics} books={featuredBooks} data-id="element-7209" data-mp="3osknntzmojwh" />}
          </motion.div>
        </AnimatePresence>

        {/* Similar Authors Section */}
        <div className={`mt-20 pt-10 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`} data-id="element-7210" data-mp="6jse9l2rovq0">
          <h2 className={`text-xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7211" data-mp="28d9ieeroveg0">
            Similar Authors
          </h2>
          <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide" data-id="element-7212" data-mp="2dccps5v55wr5">
            {similarAuthors.map(author => <Link key={author.id} to="#" className={`flex-shrink-0 w-64 p-4 rounded-xl border transition-all hover:shadow-md ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'}`} data-id="element-7213" data-mp="pq9on3k3ikrs">
                <div className="flex items-center gap-4 mb-3" data-id="element-7214" data-mp="1dfkgfvjgx4ts">
                  <img src={author.avatar} alt={author.name} className="w-14 h-14 rounded-full object-cover" data-id="element-7215" data-mp="1df18n7nj7wth" />
                  <div data-id="element-7216" data-mp="14dl2jc3r2gv2">
                    <h3 className={`font-bold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7217" data-mp="14gz5algbss31">
                      {author.name}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-amber-500" data-id="element-7218" data-mp="3k6a68zsqy9ps">
                      <StarIcon className="w-3 h-3 fill-current" data-id="element-7219" data-mp="3k8d4g741z95l" />
                      <span data-id="element-7220" data-mp="3loqz77fnuuda">{author.rating}</span>
                    </div>
                  </div>
                </div>
                <div className={`flex justify-between items-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7221" data-mp="3qlr4vh8llv3v">
                  <span data-id="element-7222" data-mp="3hkt0gxu8zlkr">{author.books} Books</span>
                  <span className={`px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-7223" data-mp="3hnoy0l26o88o">
                    {author.genre}
                  </span>
                </div>
              </Link>)}
          </div>
        </div>
      </div>
    </div>;
}